import { Autocomplete, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ClientAccountTypeForSearch, ServiceType, GetFilteredClientEntity, ClientStatusType, DateOperatorTypes } from '../../store/types';
import { EnumerationMultiSelect, ToggleButtonItem, WO2ToggleButtonGroup } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import { Props } from '../../container';
import { useDebounce } from 'src/common/hooks';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { DateRange } from 'src/store';
import { DatePickerTypes } from 'src/common/components/DateRangePickerPreDefinedDates';

const serviceTypeToggleButtons: ToggleButtonItem<string>[] = [
  { name: 'All', value: '' },
  { name: ServiceType.Mda.displayName, value: ServiceType.Mda.name },
  { name: ServiceType.Roa.displayName, value: ServiceType.Roa.name },
];

interface ReportingGenerationFormProps extends Props {
  setFlagReset: (value: boolean) => void;
}

export const ReportingGenerationForm = ({
  bulk,
  fetchFilteredReportClientsProgress,
  setServiceTypeParameter,
  setClientTypesParameter,
  fetchFilteredReportClients,
  setSelectedAfsls,
  setSelectedGroups,
  setSelectedStatuses,
  setSelectedClients,
  setSelectedPractices,
  setSelectedAdvisers,
  discardCsvUpload,
  setFlagReset,
}: ReportingGenerationFormProps) => {
  const [searchText, setSearchText] = useState<string>('');
  const [searchEntity, setSearchEntity] = useState<string>('');
  const [dateOperatorType, setDateOperatorType] = useState<DateOperatorTypes>('GreaterThan');
  const [exitDate, setExitDate] = useState<DateRange>({
    dateFrom: '',
    dateTo: '',
    dateRangeType: DatePickerTypes.DateOnly,
  });
  const [inceptionDate, setInceptionDate] = useState<DateRange>({
    dateFrom: '',
    dateTo: '',
    dateRangeType: DatePickerTypes.DateOnly,
  });

  const searchTextDebounce = useDebounce<string>(searchText, 500);

  useEffect(() => {
    fetchFilteredReportClients({
      afslIds: bulk.selectedAfsls?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      practiceIds: bulk.selectedPractices?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      adviserIds: bulk.selectedAdvisers?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      accountTypes: bulk.clientTypes ?? [],
      clientStatus: bulk.selectedStatuses ?? [],
      serviceType: bulk.serviceType ? [bulk.serviceType] : [],
      searchText: searchTextDebounce || undefined,
      searchEntity: searchTextDebounce ? searchEntity : undefined,
      exitDate: exitDate.dateTo,
      inceptionDate: inceptionDate.dateTo,
      dateOperatorType: dateOperatorType,
    });
  }, [
    searchTextDebounce,
    bulk.selectedAfsls,
    bulk.serviceType,
    bulk.clientTypes,
    bulk.selectedPractices,
    bulk.selectedAdvisers,
    bulk.selectedClients,
    bulk.selectedGroups,
    bulk.selectedStatuses,
    exitDate,
    inceptionDate,
    dateOperatorType,
  ]);

  const handleClientSearch = () => {
    fetchFilteredReportClients({
      afslIds: bulk.selectedAfsls?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      practiceIds: bulk.selectedPractices?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      adviserIds: bulk.selectedAdvisers?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      accountTypes: bulk.clientTypes ?? [],
      clientStatus: bulk.selectedStatuses ?? [],
      serviceType: bulk.serviceType ? [bulk.serviceType] : [],
      exitDate: exitDate.dateTo,
      inceptionDate: inceptionDate.dateTo,
      dateOperatorType: dateOperatorType,
    });
    discardCsvUpload();
  };

  return (
    <Grid item container direction="column" xs={12} sm={4}>
      <Grid item container direction="column" paddingRight="10px">
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            id="afsl-multiselect"
            multiple
            fullWidth
            disabled={bulk.csvUploaded || bulk.selectedClients.length > 0 || bulk.selectedGroups.length > 0}
            options={bulk.afsls}
            loading={fetchFilteredReportClientsProgress.isLoading}
            value={bulk.selectedAfsls}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                setSearchEntity('AfslLicensee');
                setSearchText(value);
              }
            }}
            getOptionLabel={(option: GetFilteredClientEntity) => option.name}
            renderInput={(params) => <TextField {...params} margin="normal" label="AFSL" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: GetFilteredClientEntity[]) => {
              setSelectedAfsls(values);
              setFlagReset(true);
            }}
            isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            id="practice-multiselect"
            fullWidth
            multiple
            options={bulk.practices}
            value={bulk.selectedPractices}
            loading={fetchFilteredReportClientsProgress.isLoading}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                setSearchEntity('Practice');
                setSearchText(value);
              }
            }}
            getOptionLabel={(option: GetFilteredClientEntity) => option.name}
            renderInput={(params) => <TextField {...params} margin="normal" label="Practice" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: GetFilteredClientEntity[]) => {
              setSelectedPractices(values);
              setFlagReset(true);
            }}
            isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            id="adviser-multiselect"
            fullWidth
            multiple
            options={bulk.advisers}
            value={bulk.selectedAdvisers}
            loading={fetchFilteredReportClientsProgress.isLoading}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                setSearchEntity('Adviser');
                setSearchText(value);
              }
            }}
            getOptionLabel={(option: GetFilteredClientEntity) => option.name}
            renderInput={(params) => <TextField {...params} margin="normal" label="Adviser" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: GetFilteredClientEntity[]) => {
              setSelectedAdvisers(values);
              setFlagReset(true);
            }}
            isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
        <Grid item>
          <EnumerationMultiSelect
            label="Account Type"
            type={ClientAccountTypeForSearch}
            value={bulk.clientTypes}
            onChange={setClientTypesParameter}
          />
        </Grid>
        <Grid item container direction="column" spacing={1} mt={1}>
          <Grid item>
            <Typography variant="h4">Service Type</Typography>
          </Grid>
          <Grid item>
            <WO2ToggleButtonGroup
              fullWidth={true}
              buttons={serviceTypeToggleButtons}
              value={bulk.serviceType}
              onChangeHandler={(value: string) => {
                setServiceTypeParameter(value);
                setFlagReset(true);
              }}
            />
          </Grid>
        </Grid>
        <Grid item>
          <EnumerationMultiSelect
            label="Statuses"
            type={ClientStatusType}
            hidden={[ClientStatusType.Terminated.name]}
            value={bulk.selectedStatuses ?? []}
            onChange={(values: string[]) => {
              setSelectedStatuses(values);
              setFlagReset(true);
            }}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            id="group-multiselect"
            multiple
            fullWidth
            disabled={bulk.csvUploaded}
            options={bulk.groups}
            value={bulk.selectedGroups}
            loading={fetchFilteredReportClientsProgress.isLoading}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                setSearchEntity('Group');
                setSearchText(value);
              }
            }}
            getOptionLabel={(option: GetFilteredClientEntity) => `${option.name} (${option.entityCoreId})`}
            renderInput={(params) => <TextField {...params} margin="normal" label="Group Search" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: GetFilteredClientEntity[]) => {
              setSelectedGroups(values);
            }}
            isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            id="client-multiselect"
            multiple
            fullWidth
            disabled={bulk.csvUploaded}
            options={bulk.clients}
            value={bulk.selectedClients}
            getOptionLabel={(option: GetFilteredClientEntity) => `${option.name} (${option.entityCoreId})`}
            loading={fetchFilteredReportClientsProgress.isLoading}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                setSearchEntity('Client');
                setSearchText(value);
              }
            }}
            renderInput={(params) => <TextField {...params} margin="normal" label="Client Search" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: GetFilteredClientEntity[]) => {
              setSelectedClients(values);
            }}
            isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
        <Grid item mt={2}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Date Operator Type</InputLabel>
            <Select
              value={dateOperatorType}
              onChange={(e) => setDateOperatorType(e.target.value as DateOperatorTypes)}
              placeholder="Date Operator Type"
              label="Date Operator Type"
            >
              <MenuItem value="GreaterThan">Greater Than</MenuItem>
              <MenuItem value="LessThan">Less Than</MenuItem>
              <MenuItem value="EqualsTo">Equals To</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Grid item mt={3}>
        <DateRangePicker dateRange={exitDate} dateOnlyLabel="Exit Date" setDateRange={setExitDate} isDateOnly />
      </Grid>
      <Grid item mt={3}>
        <DateRangePicker dateRange={inceptionDate} dateOnlyLabel="Inception Date" setDateRange={setInceptionDate} isDateOnly />
      </Grid>
      <Grid item alignSelf="flex-end" mt={1}>
        <WO2Button onClick={handleClientSearch} variant="contained">
          Search
        </WO2Button>
      </Grid>
    </Grid>
  );
};
